import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import UIContext from '../../../context/UIContext'
import DayJS from '../../../helpers/DayJS'
import downloadBlob from '../../../helpers/downloadBlob'
import { useGAEvents } from '../../../hooks/useGAEvents'
import {
    DateFilterProps,
    BasicProps,
    NetworkBasicProps,
    NetworkDateFilterProps,
    getAttendanceCsv,
    getNetworkAttendanceCSV,
    getParticipationCsv,
    getNetworkParticipationCSV,
    getValuationCsv,
    getNetworkValuationCSV,
    getPulsesCsv,
    getNetworkPulsesCSV,
    getNPSCsv,
    getNetworkNPSCSV,
    getDailyMonitoring,
    getNetworkDailyMonitoring,
    getUsersCsv,
    getNetworkUsersCSV,
    getAudioParticipationCsv,
    getNetworkAudioParticipationCSV,
} from '../Reports/apis/api-reports'
import { useReports } from '../Context/ReportsContext'
import { Error } from '../../../interfaces/error'
import { useTeam } from '../Context/TeamContext'
import { useCohorts } from './useCohorts'

export const useCsvReport = () => {
    const { t } = useTranslation('teamReports')
    const { reportQuery } = useReports()
    const { courseId, cohorts } = reportQuery
    const { toastNotification } = useContext(UIContext)
    const { sendGA } = useGAEvents()
    const { networkId } = useTeam()
    const { networkCohorts } = useCohorts()

    // Detect if we're in Network context (unified reports)
    // Only true for /network/{id}/reports or /network/{id}/reports/pdf, NOT for /network/{id}/cohorts/{cohortId}/reports
    const isNetworkContext = !!networkId && window.location.pathname.startsWith(`/network/${networkId}/reports`)

    // Map course CSV functions to their network equivalents
    const functionMap = new Map([
        [getAttendanceCsv, getNetworkAttendanceCSV],
        [getParticipationCsv, getNetworkParticipationCSV],
        [getValuationCsv, getNetworkValuationCSV],
        [getPulsesCsv, getNetworkPulsesCSV],
        [getNPSCsv, getNetworkNPSCSV],
        [getDailyMonitoring, getNetworkDailyMonitoring],
        [getUsersCsv, getNetworkUsersCSV],
        [getAudioParticipationCsv, getNetworkAudioParticipationCSV],
    ])

    const getReport = async (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fn: (query: BasicProps | NetworkBasicProps) => Promise<any>,
        csvName: string
    ) => {
        let queryParams: BasicProps | NetworkBasicProps
        let functionToCall = fn

        if (isNetworkContext && networkId) {
            // In network context, use network function and params
            const networkFunction = functionMap.get(fn)
            if (networkFunction) {
                functionToCall = networkFunction as typeof fn
            }

            // Get selected cohort IDs
            const selectedCohortIds =
                networkCohorts
                    ?.filter((nc) => cohorts?.length === 0 || cohorts?.includes(nc.startDate.substring(0, 10)))
                    .map((c) => c.courseIdMongo) || []

            queryParams = {
                networkId,
                cohorts: selectedCohortIds,
            }
        } else {
            queryParams = { courseId, cohorts }
        }

        await functionToCall(queryParams)
            .then(({ data }) => {
                toastNotification(t('loading report'), 'success')
                downloadBlob(data, `${t(csvName)}-${DayJS().format('YYYY-MM-DD_HH:mm:ss')}.csv`)
            })
            .catch((error: Error) => {
                toastNotification(
                    t(error.response.data.message ?? 'There was an error downloading the report.'),
                    'error'
                )
            })
            .finally(() =>
                sendGA({
                    category: 'Reporting',
                    action: 'GlobalReportDownload',
                })
            )
    }

    const getFilteredReport = async (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fn: (query: DateFilterProps | NetworkDateFilterProps) => Promise<any>,
        csvName: string,
        customQuery?: DateFilterProps
    ) => {
        let queryParams: DateFilterProps | NetworkDateFilterProps
        let functionToCall = fn
        const queryToUse = customQuery || reportQuery

        if (isNetworkContext && networkId) {
            // In network context, use network function and params
            const networkFunction = functionMap.get(fn)
            if (networkFunction) {
                functionToCall = networkFunction as typeof fn
            }

            // Get selected cohort IDs using customQuery if provided
            const cohortsToFilter = customQuery?.cohorts || cohorts
            const selectedCohortIds =
                networkCohorts
                    ?.filter(
                        (nc) =>
                            cohortsToFilter?.length === 0 || cohortsToFilter?.includes(nc.startDate.substring(0, 10))
                    )
                    .map((c) => c.courseIdMongo) || []

            queryParams = {
                networkId,
                cohorts: selectedCohortIds,
                from: queryToUse.from,
                to: queryToUse.to,
            }
        } else {
            queryParams = queryToUse
        }

        return await functionToCall(queryParams)
            .then(({ data }) => {
                toastNotification(t('loading report'), 'success')
                downloadBlob(data, `${t(csvName)}-${DayJS().format('YYYY-MM-DD_HH:mm:ss')}.csv`)
                return data
            })
            .catch((error: Error) => {
                toastNotification(
                    t(error.response.data.message ?? 'There was an error downloading the report.'),
                    'error'
                )
            })
            .finally(() =>
                sendGA({
                    category: 'Reporting',
                    action: 'GlobalReportDownload',
                })
            )
    }
    return { getReport, getFilteredReport }
}
